import type { ImageKitAuthentication, LocalFile } from '@frontend/api-types';
import ImageKit from 'imagekit-javascript';
import type { UploadResponse } from 'imagekit-javascript/dist/src/interfaces';

const imageKitPublicKey = 'public_yO+XJcbftfLVirjSaoMQWzAmFfQ=';
const imageKitUrlEndpoint = 'https://ik.imagekit.io/duckbill';
const imagekit = new ImageKit({
  publicKey: imageKitPublicKey,
  urlEndpoint: imageKitUrlEndpoint,
});

type ImageKitUploadResponse = UploadResponse & { contentType: string };

// Helper function to convert Blob to base64
const convertBlobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    // eslint-disable-next-line fp/no-mutation
    reader.onloadend = () => {
      const base64Data = reader.result?.toString().split(',')[1]; // Remove `data:<mime-type>;base64,` prefix
      if (base64Data) {
        resolve(base64Data);
      } else {
        reject('Could not convert blob to base64');
      }
    };
    // eslint-disable-next-line fp/no-mutation
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

// Helper to get the Blob or File for each file input
const getFileBlob = async (file: File | LocalFile): Promise<Blob> => {
  if (typeof file === 'object' && 'url' in file) {
    // If it's a LocalFile, fetch the blob from the URL (React Native)
    return fetch(file.url).then((res) => res.blob());
  } else if (file instanceof File) {
    // If it's a File object (web), use FileReader instead of fetch
    return new Promise<Blob>((resolve, reject) => {
      const reader = new FileReader();
      // eslint-disable-next-line fp/no-mutation
      reader.onload = () => {
        const result = reader.result;
        if (result) {
          // Create a Blob from the result
          const blob = new Blob([result], { type: file.type });
          resolve(blob);
        } else {
          reject(new Error('FileReader failed to read the file.'));
        }
      };
      // eslint-disable-next-line fp/no-mutation
      reader.onerror = () => reject(new Error('FileReader error.'));
      reader.readAsArrayBuffer(file);
    });
  }
  throw new Error('Unsupported file type');
};

// Upload function for LocalFile[] or File[]
export const uploadFilesToImageKit = async (
  files: (File | LocalFile)[],
  authenticateImageKit: () => Promise<ImageKitAuthentication | undefined>,
): Promise<ImageKitUploadResponse[]> => {
  const uploadedFiles: ImageKitUploadResponse[] = [];

  // Using Promise.all caused an error due to ImageKit's requirement for a unique authentication token per file upload.
  // To ensure each file upload receives a fresh token, a standard for loop is used instead.
  // eslint-disable-next-line fp/no-loops
  for (const file of files) {
    // Obtain a fresh authentication token for each upload to avoid reuse issues
    const authentication = await authenticateImageKit();
    if (!authentication) {
      throw new Error('Authentication failed for ImageKit upload.');
    }

    // Fetch the Blob of the local file
    const fileBlob = await getFileBlob(file);

    // Convert Blob to base64
    const base64File = await convertBlobToBase64(fileBlob);

    // Upload the file to ImageKit
    const uploadResponse: ImageKitUploadResponse = await new Promise((resolve, reject) => {
      imagekit.upload(
        {
          expire: authentication.expire,
          file: base64File, // Base64-encoded file content
          fileName: 'name' in file ? file.name : 'uploaded_file',
          signature: authentication.signature,
          token: authentication.token,
        },
        (error, result) => {
          if (error) {
            reject(error);
          } else {
            resolve({ ...result, contentType: fileBlob.type } as ImageKitUploadResponse);
          }
        },
      );
    });

    // eslint-disable-next-line fp/no-mutating-methods
    uploadedFiles.push(uploadResponse);
  }

  return uploadedFiles;
};

// Get image URL directly from ImageKit path
export const getImageKitImageUrl = (filePath: string): string => {
  return imagekit.url({ path: filePath });
};
